import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const primaryNavItems = [
    {
      label: 'Dashboard',
      path: '/ai-agents-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'AI-Agenten verwalten'
    },
    {
      label: 'Configuration',
      path: '/agent-configuration',
      icon: 'Settings',
      tooltip: 'Agent konfigurieren'
    },
    {
      label: 'Testing',
      path: '/chat-testing-interface',
      icon: 'MessageSquare',
      tooltip: 'Chat testen'
    },
    {
      label: 'Appearance',
      path: '/appearance-customization',
      icon: 'Palette',
      tooltip: 'Design anpassen'
    }
  ];

  const secondaryNavItems = [
    {
      label: 'Help',
      path: '/help',
      icon: 'HelpCircle'
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings'
    }
  ];

  const userMenuItems = [
    {
      label: 'Mein Profil',
      path: '/profile',
      icon: 'User'
    },
    {
      label: 'Einstellungen',
      path: '/settings',
      icon: 'Settings'
    },
    {
      label: 'Hilfe',
      path: '/help',
      icon: 'HelpCircle'
    },
    {
      type: 'divider'
    },
    {
      label: 'Abmelden',
      action: 'logout',
      icon: 'LogOut'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMoreMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleUserAction = (action) => {
    if (action === 'logout') {
      localStorage.removeItem('isAuthenticated');
      navigate('/login');
    }
    setIsUserMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className="flex items-center space-x-3">
      <div className="w-8 h-8 flex items-center justify-center">
        <img 
          src="/assets/images/Kein_Titel_900_x_1024_px_2_-1757417528542.png" 
          alt="Vierdimensional Logo" 
          className="w-8 h-8 object-contain"
        />
      </div>
      <span className="text-xl font-semibold text-secondary font-heading">
        Vierdimensional
      </span>
    </div>
  );

  return (
    <header className={`bg-card border-b border-border sticky top-0 z-50 ${className}`}>
      <div className="w-full px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Logo />

          {/* Primary Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {primaryNavItems?.map((item) => (
              <Button
                key={item?.path}
                variant={isActivePath(item?.path) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleNavigation(item?.path)}
                iconName={item?.icon}
                iconPosition="left"
                iconSize={16}
                className="relative group"
                title={item?.tooltip}
              >
                {item?.label}
              </Button>
            ))}
          </nav>

          {/* Secondary Actions */}
          <div className="flex items-center space-x-2">
            {/* More Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                iconName="MoreHorizontal"
                className="hidden md:flex"
              />
              
              {isMoreMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsMoreMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-lg z-50 animate-scale-in">
                    <div className="py-1">
                      {secondaryNavItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className="w-full px-3 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center space-x-2 transition-colors"
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                iconName="User"
                className="rounded-full w-8 h-8 p-0"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              />
              
              {isUserMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-lg z-50 animate-scale-in">
                    <div className="py-1">
                      {userMenuItems?.map((item, index) => (
                        item?.type === 'divider' ? (
                          <div key={index} className="my-1 border-t border-border" />
                        ) : (
                          <button
                            key={item?.path || item?.action}
                            onClick={() => item?.path ? handleNavigation(item?.path) : handleUserAction(item?.action)}
                            className="w-full px-3 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center space-x-2 transition-colors"
                          >
                            <Icon name={item?.icon} size={16} />
                            <span>{item?.label}</span>
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              iconName="Menu"
              className="md:hidden"
            />
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-2">
            {primaryNavItems?.map((item) => (
              <Button
                key={item?.path}
                variant={isActivePath(item?.path) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleNavigation(item?.path)}
                iconName={item?.icon}
                iconPosition="left"
                iconSize={16}
                className="justify-start"
              >
                {item?.label}
              </Button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;